import { ReactNode } from "react";
import { motion } from "framer-motion";
import { useCalculatorTheme } from "@/contexts/CalculatorThemeContext";

interface CalculatorCardProps {
  children: ReactNode;
  className?: string;
  title?: string;
  description?: string;
  noPadding?: boolean;
}

const CalculatorCard = ({ 
  children, 
  className = "", 
  title, 
  description,
  noPadding = false 
}: CalculatorCardProps) => {
  // Try to use theme context, fallback to dark if not available
  let isDark = true;
  try {
    const theme = useCalculatorTheme();
    isDark = theme.isDark;
  } catch {
    // Component is used outside of CalculatorThemeProvider, default to dark
  }

  const colors = {
    cardBg: isDark 
      ? "linear-gradient(145deg, hsla(222, 47%, 25%, 0.4), hsla(222, 47%, 8%, 0.6))"
      : "linear-gradient(145deg, hsla(0, 0%, 100%, 0.9), hsla(220, 20%, 98%, 0.95))",
    cardBorder: isDark 
      ? "hsla(45, 93%, 58%, 0.15)" 
      : "hsla(222, 47%, 15%, 0.1)",
    cardShadow: isDark 
      ? "0 10px 40px hsla(0, 0%, 0%, 0.3)" 
      : "0 10px 40px hsla(222, 47%, 15%, 0.08)",
    headerBorder: isDark 
      ? "hsla(45, 93%, 58%, 0.1)" 
      : "hsla(222, 47%, 15%, 0.08)",
    titleColor: isDark ? "hsl(0, 0%, 100%)" : "hsl(222, 47%, 15%)",
    descColor: isDark ? "hsla(0, 0%, 100%, 0.5)" : "hsla(222, 47%, 15%, 0.6)",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`rounded-2xl overflow-hidden transition-all duration-500 ${className}`}
      style={{
        background: colors.cardBg,
        border: `1px solid ${colors.cardBorder}`,
        backdropFilter: "blur(10px)",
        boxShadow: colors.cardShadow,
      }}
    >
      {(title || description) && (
        <div 
          className="px-6 py-5 border-b transition-colors duration-500"
          style={{ borderColor: colors.headerBorder }}
        >
          {title && (
            <h3 
              className="text-xl font-bold mb-1 transition-colors duration-500"
              style={{ color: colors.titleColor }}
            >
              {title}
            </h3>
          )}
          {description && (
            <p 
              className="text-sm transition-colors duration-500"
              style={{ color: colors.descColor }}
            >
              {description}
            </p>
          )}
        </div>
      )}
      <div className={noPadding ? "" : "p-6"}>
        {children}
      </div>
    </motion.div>
  );
};

export default CalculatorCard;
